import { Box, Divider, Stack, Text, Title } from '@mantine/core';
import moment from 'moment';

import { EnvironmentCountries, EORIGIN_DATE_FORMAT_WITH_TIME, InControl } from '@e-origin/shared';

import { useSettings } from '@hooks/useSettings';
import classes from './ControlNotification.module.css';
import { ControlProvidedDocuments } from './ControlProvidedDocuments';
import { ControlRequestedDocuments } from './ControlRequestedDocuments';

interface ControlNotificationProps {
  declarationId: string;
  inControl: InControl;
}

const getControlType = (controlType: string, country?: string) => {
  if (country === EnvironmentCountries.NL) {
    const controlTypeMap: Record<string, string> = {
      '10': 'Document control',
      '40': 'Physical checks',
    };

    return controlTypeMap[controlType] ? `${controlType} - ${controlTypeMap[controlType]}` : controlType;
  }

  return controlType;
};

const getNotificationType = (notificationType: string, country?: string) => {
  if (country === EnvironmentCountries.NL) {
    const notificationTypeMap: Record<string, string> = {
      '0': 'Decision to audit (and requested documents if necessary)',
      '1': 'Request for additional documents',
    };

    return notificationTypeMap[notificationType] ? `${notificationType} - ${notificationTypeMap[notificationType]}` : notificationType;
  }

  return notificationType;
};

export const ControlNotification: React.FC<ControlNotificationProps> = ({ declarationId, inControl }) => {
  const { data: settings } = useSettings();

  return (
    <>
      <Divider mb="md" />
      <Stack pt={20} pb={20}>
        <Title size="lg" fw="bold">
          Control notification
        </Title>
        <Box className={classes.row}>
          {inControl.customsRegistrationNumber && (
            <div>
              <label>Customs registration number</label>
              <p>{inControl.customsRegistrationNumber}</p>
            </div>
          )}
          <div>
            <label>Notification date</label>
            <p>{inControl.notificationDate}</p>
          </div>
          <div>
            <label>Notification Type</label>
            <p>{getNotificationType(inControl.notificationType, settings?.country)}</p>
          </div>
          <div>
            <label>Anticipated control date</label>
            <p>{inControl.anticipatedControlDate}</p>
          </div>
          <div>
            <label>Reminder expiry</label>
            <p>{moment(inControl.reminderExpiry).format(EORIGIN_DATE_FORMAT_WITH_TIME)}</p>
          </div>
        </Box>
        {inControl.text && (
          <Stack mt={15} ml={20}>
            <Text className={classes.subSectionTitle}>Additional information</Text>
            <Box className={classes.row}>
              <div>
                <label>Examination result comment</label>
                <p>{inControl.text}</p>
              </div>
            </Box>
          </Stack>
        )}
        {!!inControl.typeOfControls?.length && (
          <Stack mt={15} ml={20}>
            <Text className={classes.subSectionTitle}>Type of controls</Text>
            {inControl.typeOfControls.map((controlType, index) => (
              <Box className={classes.row} key={index}>
                <div>
                  <label>Type</label>
                  <p>{getControlType(controlType.type, settings?.country)}</p>
                </div>
                <div>
                  <label>Remarks</label>
                  <p>{controlType.remarks}</p>
                </div>
              </Box>
            ))}
          </Stack>
        )}
        {settings?.country === EnvironmentCountries.NL && (
          <>
            <Divider />
            <ControlProvidedDocuments declarationId={declarationId} providedDocuments={inControl.providedDocuments} />
            <Divider />
          </>
        )}
        <ControlRequestedDocuments requestedDocuments={inControl.requestedDocuments} />
      </Stack>
    </>
  );
};
